import { ICourseComponentPart, IUnitsDataBase, UnitChartType } from "../../../../../App/Analytics/Common";
import React, { ReactElement } from "react";
import {
  AttemptsTitle,
  ChoiceItem,
  Checkbox,
  AttemptsBefore,
  AttemptsAfter,
  AttemptsReIcon,
  AttemptsContainer,
  ChoiceGraph,
  ChoiceContainer,
  ChoiceItemContainer,
  AttemptsNote,
} from "./Attempts.styles";
import {
  isClozePart,
  isListWithOptionsPart,
  isListWithoutOptionsPart,
  isMultipleChoicePart,
  isSingleChoicePart,
  isUnorderedListPart,
} from "../../../../../components/pages/CourseAuthor/CourseInstruction/Parts/util";

import { courseInstructionPartViews } from "../../../../../utils/courseViews";
import { IChoiceExercise } from "../../../../../components/course-author/types";
import sanitizeHtml from "sanitize-html";
import { PreferredLocale } from "../../../../../utils/locales";
import { partTitle } from "../../../../../components/course-author/instruction/parts/CourseInstructionPart.helpers";
import { ReinforcementIcon } from "../../AnalyticsIcons";
import { SelectedIcon } from "../../../../../components/pages/CourseAuthor/CourseInstruction/Parts/Exercises/Choice/Item/ChoiceItem";
import { isNullOrUndefined } from "../../../../../utils";
import { UnitQuestionAnalyticsGraph, questionOptions, filteredQuestionOptions } from "./UnitInstanceAnalytics";
import { generateUnitsChartData } from "../../../../../formatters/analytics";

interface IIndividualAssessmentAnalyticsBaseProps {
  content: ICourseComponentPart;
  currentLocale: PreferredLocale;
  graphData: Record<string, IUnitsDataBase> | undefined;
}

interface IIndividualAssessmentAnalyticsFilteredProps extends IIndividualAssessmentAnalyticsBaseProps {
  filteredGraphData: Record<string, IUnitsDataBase> | undefined;
}

interface AttemptComponents {
  body: React.ReactElement | null;
  taskBefore: React.ReactElement | null;
  taskRebuke: React.ReactElement | null;
  taskReinforcement: ReactElement | null;
}

interface AttemptsComponentProps {
  components: AttemptComponents | null;
  content: ICourseComponentPart;
  isFiltered: boolean;
}

const AttemptsComponent: React.FunctionComponent<AttemptsComponentProps> = ({ components, content, isFiltered }) => {
  const testId = isFiltered ? "filtered-attempts" : "unfiltered-attempts";
  const beforeComponent = components?.taskBefore ? <AttemptsBefore>{components?.taskBefore}</AttemptsBefore> : null;
  const rebukeComponent = components?.taskRebuke ? (
    <AttemptsAfter isRebuke={true}>
      <AttemptsReIcon>
        <ReinforcementIcon rotate={true} />
      </AttemptsReIcon>
      {components?.taskRebuke}
    </AttemptsAfter>
  ) : null;
  const reinforcementComponent = components?.taskReinforcement ? (
    <AttemptsAfter isRebuke={false}>
      <AttemptsReIcon>
        <ReinforcementIcon rotate={false} />
      </AttemptsReIcon>
      {components?.taskReinforcement}
    </AttemptsAfter>
  ) : null;
  return (
    <AttemptsContainer data-testid={testId}>
      <ChoiceItemContainer>
        <AttemptsTitle>{partTitle(content)}</AttemptsTitle>
      </ChoiceItemContainer>
      {beforeComponent}
      <AttemptsTitle>Answers</AttemptsTitle>
      <AttemptsNote> *Some incorrect attempts are not displayed here for analytical purposes</AttemptsNote>
      {components?.body}
      <ChoiceItemContainer>{reinforcementComponent}</ChoiceItemContainer>
      <ChoiceItemContainer>{rebukeComponent}</ChoiceItemContainer>
    </AttemptsContainer>
  );
};

export const Attempts: React.FunctionComponent<IIndividualAssessmentAnalyticsBaseProps> = ({
  content,
  currentLocale,
  graphData,
}) => {
  if ((!isMultipleChoicePart(content) && !isSingleChoicePart(content)) || isNullOrUndefined(graphData)) {
    return null;
  }

  const components = getInstructionComponent(content, currentLocale, graphData ?? {}, {}, false);
  return <AttemptsComponent components={components} content={content} isFiltered={false} />;
};

export const FilteredAttempts: React.FunctionComponent<IIndividualAssessmentAnalyticsFilteredProps> = ({
  content,
  currentLocale,
  graphData,
  filteredGraphData,
}) => {
  if ((!isMultipleChoicePart(content) && !isSingleChoicePart(content)) || isNullOrUndefined(graphData)) {
    return null;
  }

  const components = getInstructionComponent(content, currentLocale, graphData ?? {}, filteredGraphData ?? {}, true);
  return <AttemptsComponent components={components} content={content} isFiltered={true} />;
};

const getInstructionComponent = (
  part: ICourseComponentPart,
  currentLocale: PreferredLocale,
  graphData: Record<string, IUnitsDataBase>,
  filteredGraphData: Record<string, IUnitsDataBase>,
  isFilterActive: boolean
): AttemptComponents | null => {
  if (isMultipleChoicePart(part) || isSingleChoicePart(part)) {
    const wrapping = renderExerciseWrapping(part as IChoiceExercise, currentLocale);
    return {
      body: renderChoices(part, currentLocale, isSingleChoicePart(part), graphData, filteredGraphData, isFilterActive),
      ...wrapping,
    };
  }

  if (isClozePart(part)) {
    return null;
  }

  if (isUnorderedListPart(part) || isListWithoutOptionsPart(part)) {
    return null;
  }

  if (isListWithOptionsPart(part)) {
    return null;
  }

  return null;
};

const renderExerciseWrapping = (part: IChoiceExercise, currentLocale: PreferredLocale) => {
  const rawDescription = courseInstructionPartViews.exerciseWrapping.localizedTaskBefore(part, currentLocale);
  const rawRebuke = courseInstructionPartViews.exerciseWrapping.localizedRebuke(part, currentLocale);
  const rawReinforcement = courseInstructionPartViews.exerciseWrapping.localizedReinforcement(part, currentLocale);
  const toReact = (raw: string | null, testId: string): React.ReactElement | null =>
    raw ? <span data-testid={testId} dangerouslySetInnerHTML={{ __html: sanitizeHtml(raw) }} /> : null;

  return {
    taskBefore: toReact(rawDescription, "attempts-taskBefore"),
    taskRebuke: toReact(rawRebuke, "attempts-taskRebuke"),
    taskReinforcement: toReact(rawReinforcement, "attempts-taskReinforcement"),
  };
};

const renderChoices = (
  part: IChoiceExercise,
  currentLocale: PreferredLocale,
  isSingleChoice: boolean,
  graphData: Record<string, IUnitsDataBase>,
  filteredGraphData: Record<string, IUnitsDataBase>,
  isFilterActive: boolean
): React.ReactElement | null => {
  const choices = courseInstructionPartViews.choiceExercisePart.items(part);
  if (choices === null) {
    return null;
  }
  return (
    <>
      {choices
        .filter(choice => !choice.deleted)
        .map(choice => {
          const elementByLocale = choice.text.elems.find(e => e.locale === currentLocale) || choice.text.elems[0];
          const html = sanitizeHtml(elementByLocale.value?.text);

          const emptyData = {
            Correct: { amount: 0, percentage: 0 },
            "Didn't Know": { amount: 0, percentage: 0 },
            Incorrect: { amount: 0, percentage: 0 },
          } as IUnitsDataBase;

          const data = graphData[choice.id] ? graphData[choice.id] : emptyData;
          const filteredData = filteredGraphData[choice.id] ? filteredGraphData[choice.id] : emptyData;

          const [filterFn, datum] = isFilterActive
            ? [filteredQuestionOptions, [data, filteredData]]
            : [questionOptions, [data]];

          const options = filterFn(generateUnitsChartData(datum), false, UnitChartType.Instruction, isFilterActive);

          return (
            <ChoiceContainer key={choice.id}>
              <ChoiceItemContainer>
                <ChoiceItem>
                  <Checkbox>
                    <SelectedIcon selected={choice.correct} singleChoice={isSingleChoice} />
                  </Checkbox>
                  <span data-testid="attempts-choice-item" dangerouslySetInnerHTML={{ __html: html }} />
                </ChoiceItem>
              </ChoiceItemContainer>
              <ChoiceGraph isFilterActive={isFilterActive}>
                <UnitQuestionAnalyticsGraph chartOptions={options} />
              </ChoiceGraph>
            </ChoiceContainer>
          );
        })}
    </>
  );
};
